import React, { useEffect } from "react";
import { Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "react-router-dom";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import palette from "src/theme/palette";
import { TIMEZONE_OPTIONS } from "@consts";
import { HyroButton, HyroTabs } from "@hyro-components";
import HyroBreadcrumbs from "@hyro-components/hyro-breadcrumbs/HyroBreadcrumbs";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import HyroField from "@hyro-components/fields/HyroField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import { v4 as uuidv4 } from "uuid";
import { ASSISTANT_SETTINGS_TAB_SECTIONS } from "src/pages/admin/GeneralSettingsConsts";
import {
  DEPLOYMENT_FIELDS, DRAFT_EXISTS_MESSAGE, DRAFT_NOT_EXISTS_MESSAGE,
  MIN_REPLICAS_TOOLTIP, MEMORY_TOOLTIP, ERROR_TEXTS as DEPLOYMENT_ERROR_TEXTS
} from "./deployment/DeploymentSettings.consts";
import { TABS, PAGE_TITLE } from "./AgentSettings.consts";
import PublishDialog from "src/pages/Settings/PublishDialog";

const BREADCRUMBS = ["All agents", "Agent settings"];
const BASE_URL = "/admin/all-agents";

const AgentSettingsContent = ({
  isAdminContext,
  assistantSettingsProps,
  areSettingsChanged,
  isLoading,
  isPublishDialogOpen,
  setIsPublishDialogOpen,
  onPublish,
  settingsInitialData,
  deploymentFieldProps,
  isDeploymentSubmitting,
  isReadOnly
}) => {
  const { enableUpdateTimezone } = useFlags();
  const hasPermission = useAppStore(hasPermissionSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || TABS.GENERAL;

  useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: TABS.GENERAL }, { replace: true });
    }
  }, []);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const {
    accountName, clientName, friendlyName, errorResponseText,
    agentTimezone, accountTimezone, dashboardId, skillsDashboards
  } = assistantSettingsProps;

  const timezoneOptions = TIMEZONE_OPTIONS.map((option) =>
    option.key === accountTimezone?.value ? { ...option, chipText: "Account default" } : option
  );

  const skillsDashboardsOptions = settingsInitialData?.options?.skillsDashboards;
  const options = skillsDashboardsOptions?.map(option => ({
    display_key: option,
    key: uuidv4()
  })) || [];

  return (
    <>
      {isAdminContext && <HyroBreadcrumbs breadcrumbs={BREADCRUMBS} baseUrl={BASE_URL} />}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2} mb={3} sx={{ height: 36 }}>
        <Typography variant="h5" data-testid="agent-settings-title">
          {PAGE_TITLE}
        </Typography>
        {!isReadOnly && (
          <HyroButton
            data-testid="agent-settings-save-changes"
            loading={isLoading}
            onClick={() => setIsPublishDialogOpen(true)}
            disabled={!areSettingsChanged}
            variant={BUTTON_VARIANTS.CONTAINED}
            color={BUTTON_COLORS.PRIMARY}
          >
            Save changes
          </HyroButton>
        )}
      </Stack>

      <HyroTabs
        selectedValue={activeTab}
        onTabChange={handleTabChange}
        tabsSpec={[
          {
            label: "General",
            value: TABS.GENERAL,
            content: (
              <Stack direction="column" gap={2}>
                <Typography variant="h6">General settings</Typography>

                <Stack direction="column" gap={3}>
                  <Box sx={{ bgcolor: palette.system[0], border: 1, borderColor: palette.grey[200], borderRadius: 1, p: 3 }}>
                    <Box sx={{ maxWidth: 552 }}>
                      <Stack direction="column" gap="24px">
                        <HyroField
                          fieldType={FIELD_TYPES.TEXT}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.ACCOUNT}
                          value={accountName?.value}
                          isReadOnly
                          showTopSection
                          isFieldReadOnlyFromSpec={true}
                        />

                        <HyroField
                          fieldType={FIELD_TYPES.TEXT}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.CONFIGURATION_ID}
                          value={clientName?.value}
                          isReadOnly
                          showTopSection
                          isFieldReadOnlyFromSpec={true}
                        />

                        <HyroField
                          testId="assistant-name-input"
                          fieldType={FIELD_TYPES.TEXT}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.ASSISTANT_NAME}
                          placeholder={ASSISTANT_SETTINGS_TAB_SECTIONS.ASSISTANT_NAME}
                          value={friendlyName?.value}
                          onChange={friendlyName?.handleChange}
                          onBlur={() => friendlyName?.setErrorText(friendlyName?.value ? "" : "Please enter a name")}
                          error={friendlyName?.errorText}
                          isReadOnly={isReadOnly}
                          showTopSection
                        />

                        <HyroField
                          fieldType={FIELD_TYPES.TEXT}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.ERROR_RESPONSE_TEXT}
                          placeholder="Enter error response text"
                          value={errorResponseText?.value}
                          onChange={errorResponseText?.handleChange}
                          isReadOnly={isReadOnly}
                          showTopSection
                        />

                        {enableUpdateTimezone && (
                          <HyroField
                            fieldType={FIELD_TYPES.CHOOSE_ONE}
                            onChange={agentTimezone?.handleChange}
                            value={agentTimezone?.value || accountTimezone?.value}
                            data-testid="assistant-timezone-select"
                            name="agentTimezone"
                            label="Time zone"
                            description="Overrides account default for this agent."
                            options={timezoneOptions}
                            isReadOnly={isReadOnly}
                            fullWidth
                            showTopSection
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  <Box sx={{ bgcolor: palette.system[0], border: 1, borderColor: palette.grey[200], borderRadius: 1, p: 3 }}>
                    <Typography variant="subtitle1" mb={2}>{ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.TITLE}</Typography>
                    <Box sx={{ maxWidth: 552 }}>
                      <Stack direction="column" gap="24px">
                        <HyroField
                          fieldType={FIELD_TYPES.TEXT}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.DASHBOARD_ID}
                          placeholder={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.DASHBOARD_ID}
                          value={dashboardId?.value}
                          error={dashboardId?.errorText}
                          onChange={dashboardId?.handleChange}
                          isReadOnly={isReadOnly}
                          showTopSection
                        />

                        <HyroField
                          fieldType={FIELD_TYPES.CHOOSE_AT_LEAST_ONE}
                          label={ASSISTANT_SETTINGS_TAB_SECTIONS.LOOKER.FIELDS.SKILLS_DASHBOARDS}
                          placeholder="Select a skill"
                          value={skillsDashboards?.value}
                          onChange={skillsDashboards?.handleChange}
                          options={options}
                          error={skillsDashboards?.errorText}
                          isReadOnly={isReadOnly}
                          showTopSection
                        />
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Stack>
            )
          },
          ...hasPermission(USER_PERMISSIONS.ABILITIES.DEPLOYMENT_SETTINGS) ? [{
            label: "Deployment",
            value: TABS.DEPLOYMENT,
            content: (
              <Stack direction="column" gap={2}>
                <Stack direction="column" gap="2px">
                  <Typography variant="h6">Deployment settings</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deploymentFieldProps?.draftExists ? DRAFT_EXISTS_MESSAGE : DRAFT_NOT_EXISTS_MESSAGE}
                  </Typography>
                </Stack>

                <Box sx={{ bgcolor: palette.system[0], border: 1, borderColor: palette.grey[200], borderRadius: 1, p: 3 }}>
                  <Box sx={{ maxWidth: 552 }}>
                    <Stack direction="column" gap="24px">
                      <HyroField
                        fieldType={FIELD_TYPES.TEXT}
                        label={DEPLOYMENT_FIELDS.MIN_REPLICAS.label}
                        placeholder={DEPLOYMENT_FIELDS.MIN_REPLICAS.label}
                        tooltip={MIN_REPLICAS_TOOLTIP}
                        value={deploymentFieldProps?.minReplicas?.value}
                        onChange={(val) => {
                          if (val === "" || /^\d*$/.test(val)) {
                            deploymentFieldProps?.minReplicas?.handleChange(val);
                            const numVal = parseInt(val);
                            if (val === "") {
                              deploymentFieldProps?.minReplicas?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MIN_REPLICAS_REQUIRED);
                            } else if (numVal >= DEPLOYMENT_FIELDS.MIN_REPLICAS.min && numVal <= DEPLOYMENT_FIELDS.MIN_REPLICAS.max) {
                              deploymentFieldProps?.minReplicas?.setErrorText("");
                            } else if (numVal < DEPLOYMENT_FIELDS.MIN_REPLICAS.min) {
                              deploymentFieldProps?.minReplicas?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MIN_REPLICAS_MIN);
                            } else if (numVal > DEPLOYMENT_FIELDS.MIN_REPLICAS.max) {
                              deploymentFieldProps?.minReplicas?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MIN_REPLICAS_MAX);
                            }
                          }
                        }}
                        error={deploymentFieldProps?.minReplicas?.errorText}
                        isReadOnly={isDeploymentSubmitting || isReadOnly}
                        showTopSection
                      />

                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!deploymentFieldProps?.forceMinReplicas?.value}
                            onChange={(e) => deploymentFieldProps?.forceMinReplicas?.handleChange(e.target.checked)}
                            disabled={isDeploymentSubmitting || isReadOnly}
                          />
                        }
                        label={DEPLOYMENT_FIELDS.FORCE_MIN_REPLICAS.label}
                      />

                      <HyroField
                        fieldType={FIELD_TYPES.TEXT}
                        label={DEPLOYMENT_FIELDS.MEMORY.label}
                        placeholder={DEPLOYMENT_FIELDS.MEMORY.label}
                        tooltip={MEMORY_TOOLTIP}
                        value={deploymentFieldProps?.memory?.value}
                        onChange={(val) => {
                          if (val === "" || /^\d*$/.test(val)) {
                            deploymentFieldProps?.memory?.handleChange(val);
                            const numVal = parseInt(val);
                            if (val === "") {
                              deploymentFieldProps?.memory?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MEMORY_REQUIRED);
                            } else if (numVal >= DEPLOYMENT_FIELDS.MEMORY.min && numVal <= DEPLOYMENT_FIELDS.MEMORY.max) {
                              deploymentFieldProps?.memory?.setErrorText("");
                            } else if (numVal < DEPLOYMENT_FIELDS.MEMORY.min) {
                              deploymentFieldProps?.memory?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MEMORY_MIN);
                            } else if (numVal > DEPLOYMENT_FIELDS.MEMORY.max) {
                              deploymentFieldProps?.memory?.setErrorText(DEPLOYMENT_ERROR_TEXTS.MEMORY_MAX);
                            }
                          }
                        }}
                        error={deploymentFieldProps?.memory?.errorText}
                        isReadOnly={isDeploymentSubmitting || isReadOnly}
                        showTopSection
                      />

                      <HyroField
                        fieldType={FIELD_TYPES.TEXT}
                        label={DEPLOYMENT_FIELDS.CPU.label}
                        value={deploymentFieldProps?.cpu?.value}
                        isReadOnly
                        isFieldReadOnlyFromSpec
                        showTopSection
                      />

                      <HyroField
                        fieldType={FIELD_TYPES.TEXT}
                        label={DEPLOYMENT_FIELDS.DEDICATED.label}
                        value={deploymentFieldProps?.dedicated?.value}
                        isReadOnly
                        isFieldReadOnlyFromSpec
                        showTopSection
                      />
                    </Stack>
                  </Box>
                </Box>
              </Stack>
            )
          }] : []
        ]}
      />

      <PublishDialog
        isOpen={isPublishDialogOpen}
        isLoading={isLoading}
        onCancel={() => setIsPublishDialogOpen(false)}
        onSumbit={onPublish}
        title="Publish Settings Changes"
      />
    </>
  );
};

export default AgentSettingsContent;
